import behaviourPointHistoryConstants from "@/constants/behaviourPointHistoryConstants.js"
import behaviourPointsConstants from "@/constants/behaviourPointsConstants.js"
import behaviourStudentListConstants from "@/constants/behaviourStudentListConstants.js"
import { parsePbisCsvExportResponse } from "@/helpers/behaviourCsvExport.js"
import {
  buildPointHistoryExportParams,
  buildPointHistoryTransactionsParams,
  parsePointHistoryTransactionsResponse,
  parsePointsSummaryResponse,
  parseStudentPointsSummaryResponse
} from "@/helpers/behaviourPointHistoryApi.js"
import {
  buildStaffStudentSummaryParams,
  buildStaffStudentTransactionsExportParams,
  buildStaffStudentTransactionsParams,
  buildStudentListExportParams,
  buildStudentListParams,
  parseStaffStudentTransactionsResponse,
  parseStudentListResponse,
  staffStudentSummaryPath,
  staffStudentTransactionsExportPath,
  staffStudentTransactionsPath
} from "@/helpers/behaviourStudentListApi.js"
import {
  resolvePointHistoryExportApiPath,
  resolvePointHistoryTransactionsApiPath
} from "@/helpers/behaviourPbisApiPaths.js"
import {
  buildAwardPointsPayload,
  buildCorrectionPointsPayload,
  buildRedeemPointsPayload,
  parseBehaviourPointsSubmitResponse
} from "@/helpers/behaviourPointsForm.js"
import { apiV8nocache } from "@/store/modules/axiousWithCache"

const state = () => ({
  pbisPointsMutationSignal: {
    nonce: 0,
    studentIds: []
  }
})

const getters = {
  pbisPointsMutationSignal: (state) => state.pbisPointsMutationSignal
}

const mutations = {
  notifyPbisPointsMutated(state, { studentIds = [] } = {}) {
    state.pbisPointsMutationSignal = {
      nonce: state.pbisPointsMutationSignal.nonce + 1,
      studentIds: Array.isArray(studentIds) ? studentIds : []
    }
  }
}

const actions = {
  /**
   * My history → `/api/v8/pbis/transactions`;
   * school-wide → `/api/v8/admin/pbis/transactions`;
   * students → `/api/v8/student/pbis/points/transactions`.
   * `query.scope` only selects the path; it is not sent as a query param.
   * @param {{
   *   scope?: "mine" | "schoolWide",
   *   fromDate: string,
   *   toDate: string,
   *   page?: number,
   *   perPage?: number,
   *   search?: string,
   *   gradYears?: unknown,
   *   coreValueId?: string | number | null,
   *   awardedByUserIds?: unknown
   * }} query
   * @returns {Promise<{ rows: Record<string, unknown>[], pagination: Record<string, unknown>, stats: Record<string, number> }>}
   */
  async fetchPointHistoryTransactions({ rootGetters }, query = {}) {
    const role = rootGetters["authentication/getUserRole"]
    const path = resolvePointHistoryTransactionsApiPath(role, query?.scope)
    const params = buildPointHistoryTransactionsParams(query)
    const response = await apiV8nocache.get(path, { params })
    return parsePointHistoryTransactionsResponse(response?.data)
  },

  /**
   * My history → `/api/v8/pbis/transactions/export`;
   * school-wide → `/api/v8/admin/pbis/transactions/export`.
   * `query.scope` only selects the path; it is not sent as a query param.
   * @param {{ scope?: "mine" | "schoolWide" } & Parameters<typeof buildPointHistoryTransactionsParams>[0]} query
   * @returns {Promise<{ url: string, fileName: string, csvContent: string }>}
   */
  async exportPointHistoryTransactions(_, query = {}) {
    const path = resolvePointHistoryExportApiPath(query?.scope)
    const params = buildPointHistoryExportParams(query)
    const response = await apiV8nocache.get(path, { params })
    return parsePbisCsvExportResponse(response?.data)
  },

  /**
   * GET /api/v8/admin/pbis/reports/points/summary
   * @returns {Promise<{ pointsAwarded: number, pointsRedeemed: number }>}
   */
  async fetchSchoolWidePointsSummary() {
    const response = await apiV8nocache.get(
      behaviourPointHistoryConstants.SCHOOL_WIDE_POINTS_SUMMARY_API_PATH
    )
    return parsePointsSummaryResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/reports/points/summary/mine
   * @returns {Promise<{ pointsAwarded: number, pointsRedeemed: number }>}
   */
  async fetchMyPointsSummary() {
    const response = await apiV8nocache.get(
      behaviourPointHistoryConstants.MY_POINTS_SUMMARY_API_PATH
    )
    return parsePointsSummaryResponse(response?.data)
  },

  /**
   * GET /api/v8/student/pbis/points/summary
   * @returns {Promise<{ totalPointsEarned: number, pointsUsedOnRewards: number, remainingPoints: number }>}
   */
  async fetchStudentPointsSummary() {
    const response = await apiV8nocache.get(
      behaviourPointHistoryConstants.STUDENT_POINTS_SUMMARY_API_PATH
    )
    return parseStudentPointsSummaryResponse(response?.data)
  },

  /**
   * POST /api/v8/pbis/points/awards
   * @param {Record<string, unknown>} form
   * @returns {Promise<{ transactionIds: number[], count: number }>}
   */
  async createPointsAward(_context, form) {
    const payload = buildAwardPointsPayload(form)
    const response = await apiV8nocache.post(
      behaviourPointsConstants.AWARDS_API_PATH,
      payload
    )
    return parseBehaviourPointsSubmitResponse(response?.data)
  },

  /**
   * POST /api/v8/pbis/points/redemptions
   * @param {Record<string, unknown>} form
   * @returns {Promise<{ transactionIds: number[], count: number }>}
   */
  async createPointsRedemption(_context, form) {
    const payload = buildRedeemPointsPayload(form)
    const response = await apiV8nocache.post(
      behaviourPointsConstants.REDEMPTIONS_API_PATH,
      payload
    )
    return parseBehaviourPointsSubmitResponse(response?.data)
  },

  /**
   * POST /api/v8/pbis/points/corrections
   * @param {Record<string, unknown>} form
   * @returns {Promise<{ transactionIds: number[], count: number }>}
   */
  async createPointsCorrection(_context, form) {
    const payload = buildCorrectionPointsPayload(form)
    const response = await apiV8nocache.post(
      behaviourPointsConstants.CORRECTIONS_API_PATH,
      payload
    )
    return parseBehaviourPointsSubmitResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/reports/students
   * @param {{
   *   page?: number,
   *   perPage?: number,
   *   search?: string,
   *   gradYears?: unknown
   * }} query
   * @returns {Promise<{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }>}
   */
  async fetchStudentList(_context, query = {}) {
    const params = buildStudentListParams(query)
    const response = await apiV8nocache.get(
      behaviourStudentListConstants.STUDENTS_REPORT_API_PATH,
      { params }
    )
    return parseStudentListResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/reports/students/export
   * @param {Parameters<typeof buildStudentListParams>[0]} query
   * @returns {Promise<{ url: string, fileName: string, csvContent: string }>}
   */
  async exportStudentList(_context, query = {}) {
    const params = buildStudentListExportParams(query)
    const response = await apiV8nocache.get(
      behaviourStudentListConstants.STUDENTS_REPORT_EXPORT_API_PATH,
      { params }
    )
    return parsePbisCsvExportResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/students/{studentId}/points/summary
   * @param {{ studentId: number | string, fromDate?: string, toDate?: string }} query
   */
  async fetchStaffStudentPointsSummary(_context, query = {}) {
    const path = staffStudentSummaryPath(query?.studentId)
    if (!path) {
      return {
        totalPointsEarned: 0,
        pointsUsedOnRewards: 0,
        remainingPoints: 0
      }
    }

    const params = buildStaffStudentSummaryParams(query?.studentId, {
      fromDate: query?.fromDate,
      toDate: query?.toDate
    })
    const response = await apiV8nocache.get(path, { params })
    return parseStudentPointsSummaryResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/students/{studentId}/points/transactions
   * @param {{
   *   studentId: number | string,
   *   fromDate?: string,
   *   toDate?: string,
   *   page?: number,
   *   perPage?: number,
   *   search?: string,
   *   coreValueId?: string | number | null,
   *   sortBy?: string|null,
   *   sortOrder?: string|null
   * }} query
   */
  async fetchStaffStudentPointTransactions(_context, query = {}) {
    const path = staffStudentTransactionsPath(query?.studentId)
    if (!path) {
      return { rows: [], pagination: {} }
    }

    const params = buildStaffStudentTransactionsParams(query?.studentId, {
      fromDate: query?.fromDate,
      toDate: query?.toDate,
      page: query?.page,
      perPage: query?.perPage,
      search: query?.search,
      coreValueId: query?.coreValueId,
      sortBy: query?.sortBy,
      sortOrder: query?.sortOrder
    })
    const response = await apiV8nocache.get(path, { params })
    return parseStaffStudentTransactionsResponse(response?.data)
  },

  /**
   * GET /api/v8/pbis/students/{studentId}/points/transactions/export
   * @param {Parameters<typeof buildStaffStudentTransactionsExportParams>[1] & { studentId: number | string }} query
   * @returns {Promise<{ url: string, fileName: string, csvContent: string }>}
   */
  async exportStaffStudentPointTransactions(_context, query = {}) {
    const path = staffStudentTransactionsExportPath(query?.studentId)
    if (!path) {
      return { url: "", fileName: "", csvContent: "" }
    }

    const params = buildStaffStudentTransactionsExportParams(query?.studentId, {
      fromDate: query?.fromDate,
      toDate: query?.toDate,
      page: query?.page,
      perPage: query?.perPage,
      search: query?.search,
      coreValueId: query?.coreValueId,
      sortBy: query?.sortBy,
      sortOrder: query?.sortOrder
    })
    const response = await apiV8nocache.get(path, { params })
    return parsePbisCsvExportResponse(response?.data)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
